import {
  formatLogisticsV2AllowedIncentiveTypes,
  isLogisticsV2IncentiveTypeAllowed,
  isLogisticsV2IncentiveUiEnabled,
  resolveLogisticsV2AllowedIncentiveTypes,
} from './mc-incentive.model';

/** Sub-features for logistics v2 create flow (payroll-dash). */
export interface LogisticsV2IncentiveFeatures {
  versionPayoutUi?: boolean;
  mcSignIn?: boolean;
  /** When true (default), HH checkbox is hidden for incentive version v2. */
  hhOnV1Only?: boolean;
  qualityConditions?: boolean;
  qualitySlabCondition?: boolean;
  deferredPayoutPolicy?: boolean;
  slotTitles?: boolean;
}

export type LogisticsV2IncentiveFeatureKey = keyof Required<LogisticsV2IncentiveFeatures>;

export interface LogisticsV2IncentiveBlock {
  enabled?: boolean;
  enabledServices?: string[];
  allowedIncentiveTypes?: string[];
  features?: LogisticsV2IncentiveFeatures;
}

/** Environment / window.__env shape (flat service keys still supported). */
export interface LogisticsV2IncentiveEnv {
  logisticsV2Incentive?: LogisticsV2IncentiveBlock;
  logisticsV2IncentiveEnabledServices?: string[];
}

const DEFAULT_FEATURES: Required<LogisticsV2IncentiveFeatures> = {
  versionPayoutUi: true,
  mcSignIn: true,
  hhOnV1Only: true,
  qualityConditions: true,
  qualitySlabCondition: true,
  deferredPayoutPolicy: true,
  slotTitles: true,
};

export interface ResolvedLogisticsV2IncentiveConfig {
  enabled: boolean;
  enabledServices: string[];
  allowedIncentiveTypes: string[];
  features: Required<LogisticsV2IncentiveFeatures>;
}

function resolveEnabledServices(env: LogisticsV2IncentiveEnv): string[] {
  const block = env.logisticsV2Incentive;
  if (block && block.enabledServices && block.enabledServices.length) {
    return block.enabledServices;
  }
  const primary = env.logisticsV2IncentiveEnabledServices;
  return primary && primary.length ? primary : ['all'];
}

function resolveAllowedTypes(env: LogisticsV2IncentiveEnv): string[] {
  const block = env.logisticsV2Incentive;
  return resolveLogisticsV2AllowedIncentiveTypes(block && block.allowedIncentiveTypes);
}

/** Single resolved config for v2 incentive UI and validation helpers. */
export function resolveLogisticsV2IncentiveConfig(
  env: LogisticsV2IncentiveEnv,
): ResolvedLogisticsV2IncentiveConfig {
  const block = env.logisticsV2Incentive || {};
  const enabled = block.enabled !== false;
  const features = { ...DEFAULT_FEATURES, ...(block.features || {}) };
  return {
    enabled,
    enabledServices: resolveEnabledServices(env),
    allowedIncentiveTypes: resolveAllowedTypes(env),
    features,
  };
}

export function isLogisticsV2MasterEnabled(env: LogisticsV2IncentiveEnv): boolean {
  return resolveLogisticsV2IncentiveConfig(env).enabled;
}

export function isLogisticsV2FeatureEnabled(
  env: LogisticsV2IncentiveEnv,
  feature: LogisticsV2IncentiveFeatureKey,
): boolean {
  const cfg = resolveLogisticsV2IncentiveConfig(env);
  if (!cfg.enabled) {
    return false;
  }
  return cfg.features[feature];
}

/** Service allowlist met for showing v2 create UI (version, payout, MC). */
export function isLogisticsV2CreateUiEnabledForServices(
  env: LogisticsV2IncentiveEnv,
  selectedServices: any[] | string | null | undefined,
): boolean {
  const cfg = resolveLogisticsV2IncentiveConfig(env);
  if (!cfg.enabled) {
    return false;
  }
  return isLogisticsV2IncentiveUiEnabled(cfg.enabledServices, selectedServices);
}

export {
  formatLogisticsV2AllowedIncentiveTypes,
  isLogisticsV2IncentiveTypeAllowed,
};
